import { supabase } from '../lib/supabase';

export interface Partner {
  id: string;
  business_name: string;
  contact_email: string;
  contact_phone: string | null;
  contact_name: string | null;
  billing_address: string | null;
  business_address: string | null;
  vat_number: string | null;
  business_description: string | null;
  business_license_number: string | null;
  business_logo_url: string | null;
  website_url: string | null;
  social_media_urls: Record<string, any>;
  commission_rate: number;
  partnership_tier: string;
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  tax_id: string | null;
  notes: string | null;
  application_date: string;
  approved_date: string | null;
  approved_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartnerDocument {
  id: string;
  partner_id: string;
  document_type: string;
  document_url: string;
  uploaded_at: string;
  verified: boolean;
}

export interface PartnerApplication {
  partner: Partner;
  documents: PartnerDocument[];
  offers_count: number;
}

// Get total partners count (users with partner role)
export const getTotalPartnersCount = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('user_profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'partner');

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching total partners count:', error);
    throw error;
  }
};

// Get all partners (for admin) - UPDATED to show all statuses including suspended
export const getAllPartners = async (): Promise<Partner[]> => {
  try {
    const { data: partners, error } = await supabase
      .from('partners')
      .select('*')
      .in('status', ['active', 'suspended', 'rejected']) // Show active, suspended, and rejected partners
      .order('created_at', { ascending: false });

    if (error) throw error;
    return partners || [];
  } catch (error) {
    console.error('Error fetching all partners:', error);
    throw error;
  }
};

export async function createPartner(
  partnerData: Omit<Partner, 'id' | 'status' | 'application_date' | 'created_at' | 'updated_at' | 'readable_id'>
): Promise<{ partner: Partner | null; error: string | null }> {
  try {
    console.log('🚀 Creating partner with data:', partnerData);

    // REMOVED: Client-side auth.admin.listUsers() check (security violation)
    // The database function handles all duplicate detection securely

    // Call database function to create partner with built-in duplicate detection
    const { data, error } = await supabase.rpc('admin_create_partner_with_auth', {
      p_business_name: partnerData.business_name,
      p_contact_email: partnerData.contact_email,
      p_billing_address: partnerData.billing_address || '',
      p_vat_number: partnerData.vat_number || '',
      p_contact_phone: partnerData.contact_phone || '',
      p_contact_name: partnerData.contact_name || '',
      p_temp_password: 'TravelHero2024!'
    });

    if (error) {
      console.error('❌ Database function error:', error);
      return { 
        partner: null, 
        error: `Database error: ${error.message}` 
      };
    }

    console.log('📦 Function response:', data);

    // The function returns an array with a single result object
    const result = Array.isArray(data) ? data[0] : data;

    if (!result) {
      return { 
        partner: null, 
        error: 'No response from database function' 
      };
    }

    // CRITICAL FIX: Check if the operation was successful
    if (!result.success) {
      console.error('❌ Partner creation returned success: false:', result.message);
      
      // Parse error message to identify duplicate email errors
      const errorMessage = result.message || 'Partner creation failed';
      
      // CRITICAL: Define duplicate email indicators
      const duplicateIndicators = [
        'already registered',
        'race condition',
        'conflicts with an existing record',
        'already exists',
        'user profile already exists',
        'duplicate'
      ];
      
      // Check if this is a duplicate email error (which should be treated as success)
      const isDuplicateError = duplicateIndicators.some(indicator => 
        errorMessage.toLowerCase().includes(indicator.toLowerCase())
      );
      
      if (isDuplicateError) {
        console.log('✅ Duplicate email detected - treating as informational success');
        
        // Try to fetch the existing partner by email
        const { data: existingPartner, error: fetchError } = await supabase
          .from('partners')
          .select('*')
          .eq('contact_email', partnerData.contact_email)
          .eq('status', 'active')
          .maybeSingle();

        if (fetchError) {
          console.warn('Could not fetch existing partner:', fetchError);
        }

        if (existingPartner) {
          // Partner found and active - return as success
          console.log('✅ Found existing active partner:', existingPartner);
          return { 
            partner: existingPartner, 
            error: null 
          };
        }

        // Partner not found or not active - this is actually a problem
        return { 
          partner: null, 
          error: `The email "${partnerData.contact_email}" is already registered in the system but the partner account could not be activated. Please contact support.`
        };
      }
      
      // Not a duplicate error - this is a genuine failure
      return { 
        partner: null, 
        error: `Partner creation failed: ${errorMessage}`
      };
    }

    // SUCCESS case - fetch the created partner record
    const { data: partner, error: fetchError } = await supabase
      .from('partners')
      .select('*')
      .eq('id', result.partner_id)
      .single();

    if (fetchError || !partner) {
      console.error('❌ Failed to fetch created partner:', fetchError);
      return { 
        partner: null, 
        error: 'Partner created but failed to retrieve details' 
      };
    }

    console.log('✅ Partner created successfully:', partner);
    return { partner, error: null };

  } catch (err) {
    console.error('❌ Unexpected error in createPartner:', err);
    const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
    
    // CRITICAL: Also check caught errors for duplicate indicators
    const duplicateIndicators = [
      'already registered',
      'race condition',
      'conflicts with an existing record',
      'already exists',
      'user profile already exists',
      'duplicate'
    ];
    
    const isDuplicateError = duplicateIndicators.some(indicator => 
      errorMessage.toLowerCase().includes(indicator.toLowerCase())
    );
    
    if (isDuplicateError) {
      console.log('✅ Caught duplicate error in exception handler');
      
      // Try to fetch the existing partner
      const { data: existingPartner } = await supabase
        .from('partners')
        .select('*')
        .eq('contact_email', partnerData.contact_email)
        .eq('status', 'active')
        .maybeSingle();

      if (existingPartner) {
        return { partner: existingPartner, error: null };
      }
    }
    
    return { 
      partner: null, 
      error: errorMessage 
    };
  }
}

// Activate a partner (change status from suspended to active)
export const activatePartner = async (partnerId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('partners')
      .update({
        status: 'active',
        updated_at: new Date().toISOString()
      })
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error activating partner:', error);
    throw error;
  }
};

// Suspend a partner
export const suspendPartner = async (partnerId: string, reason?: string): Promise<void> => {
  try {
    const updateData: any = {
      status: 'suspended',
      updated_at: new Date().toISOString()
    };

    if (reason) {
      updateData.notes = reason;
    }

    const { error } = await supabase
      .from('partners')
      .update(updateData)
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error suspending partner:', error);
    throw error;
  }
};

// Get current partner info (for logged-in partner)
export const getCurrentPartnerInfo = async (): Promise<Partner | null> => {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return null;
    }
    
    // Get partner_id from user_profiles table
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('partner_id, role')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    if (!profile || !profile.partner_id) {
      console.log('No partner_id found in user profile');
      return null;
    }

    if (profile.role !== 'partner') {
      console.log('User is not a partner');
      return null;
    }

    // Fetch partner details
    const { data: partner, error: partnerError } = await supabase
      .from('partners')
      .select('*')
      .eq('id', profile.partner_id)
      .single();

    if (partnerError) {
      console.error('Error fetching partner:', partnerError);
      throw partnerError;
    }

    return partner;
  } catch (error) {
    console.error('Error in getCurrentPartnerInfo:', error);
    throw error;
  }
};

// Get partner offer performance metrics using database function with date filtering
export const getPartnerOfferPerformance = async (
  partnerId: string,
  startDate?: Date,
  endDate?: Date
) => {
  try {
    // Build function parameters
    const params: any = {
      partner_id_param: partnerId
    };

    // Add date parameters if provided
    if (startDate) {
      params.start_date = startDate.toISOString();
    }
    if (endDate) {
      params.end_date = endDate.toISOString();
    }

    // Call date-filtered database function
    const { data, error } = await supabase
      .rpc('get_partner_offer_performance_filtered', params);

    if (error) throw error;

    if (!data || data.length === 0) {
      return {
        totalOffers: 0,
        totalRedemptions: 0,
        totalRevenue: 0,
        totalSavings: 0,
        averageRedemptionsPerOffer: 0,
        offerPerformance: []
      };
    }

    // Map database function results to match component interface
    const offerPerformance = data.map((offer: any) => ({
      offerId: offer.offer_id,
      offerTitle: offer.offer_title,
      offerStatus: offer.offer_status,
      redemptionCount: offer.redemption_count || 0,
      savings: parseFloat(offer.total_savings || 0).toFixed(2),
      lastRedemptionDate: offer.last_redemption_date,
      revenue: 0, // Not returned by function, can be calculated separately if needed
      category: '', // Not returned by function
      discountValue: '' // Not returned by function
    }));

    // Calculate totals from aggregated data
    const totalRedemptions = offerPerformance.reduce((sum: number, o: any) => sum + o.redemptionCount, 0);
    const totalSavings = offerPerformance.reduce((sum: number, o: any) => sum + parseFloat(o.savings), 0);

    return {
      totalOffers: data.length,
      totalRedemptions,
      totalRevenue: 0, // Not calculated by this function
      totalSavings: totalSavings.toFixed(2),
      averageRedemptionsPerOffer: data.length > 0 ? (totalRedemptions / data.length).toFixed(2) : '0',
      offerPerformance: offerPerformance.sort((a: any, b: any) => b.redemptionCount - a.redemptionCount)
    };
  } catch (error) {
    console.error('Error fetching partner offer performance:', error);
    throw error;
  }
};

// Get all pending partner applications
export const getPendingApplications = async (): Promise<PartnerApplication[]> => {
  try {
    // Fetch both 'pending' and 'pre_approved' partners for onboarding workflow
    const { data: partners, error: partnersError } = await supabase
      .from('partners')
      .select('*')
      .in('status', ['pending', 'pre_approved']) // Include both statuses
      .order('application_date', { ascending: false });

    if (partnersError) throw partnersError;
    if (!partners) return [];

    // Get offers count for each partner
    const applicationsWithCounts = await Promise.all(
      partners.map(async (partner) => {
        const { count } = await supabase
          .from('offers')
          .select('*', { count: 'exact', head: true })
          .eq('partner_id', partner.id);

        return {
          partner,
          documents: [], // Documents would be fetched from a separate storage bucket
          offers_count: count || 0
        };
      })
    );

    return applicationsWithCounts;
  } catch (error) {
    console.error('Error fetching pending applications:', error);
    throw error;
  }
};

// Get partner by ID with full details
export const getPartnerById = async (partnerId: string): Promise<PartnerApplication | null> => {
  try {
    const { data: partner, error: partnerError } = await supabase
      .from('partners')
      .select('*')
      .eq('id', partnerId)
      .single();

    if (partnerError) throw partnerError;
    if (!partner) return null;

    // Get offers count
    const { count } = await supabase
      .from('offers')
      .select('*', { count: 'exact', head: true })
      .eq('partner_id', partnerId);

    return {
      partner,
      documents: [], // Documents would be fetched from storage
      offers_count: count || 0
    };
  } catch (error) {
    console.error('Error fetching partner details:', error);
    throw error;
  }
};

// Approve partner application
export const approvePartner = async (
  partnerId: string,
  approvedBy: string,
  commissionRate?: number
): Promise<void> => {
  try {
    const updateData: any = {
      status: 'active',
      approved_by: approvedBy,
      approved_date: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (commissionRate !== undefined) {
      updateData.commission_rate = commissionRate;
    }

    const { error } = await supabase
      .from('partners')
      .update(updateData)
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error approving partner:', error);
    throw error;
  }
};

// Reject partner application
export const rejectPartner = async (
  partnerId: string,
  rejectionReason: string
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('partners')
      .update({
        status: 'rejected',
        notes: rejectionReason,
        updated_at: new Date().toISOString()
      })
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error rejecting partner:', error);
    throw error;
  }
};

// Update partner commission rate
export const updatePartnerCommission = async (
  partnerId: string,
  commissionRate: number
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('partners')
      .update({
        commission_rate: commissionRate,
        updated_at: new Date().toISOString()
      })
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating commission rate:', error);
    throw error;
  }
};

// Create partner account (for system integration)
export const createPartnerAccount = async (data: {
  businessName: string;
  contactEmail: string;
  contactName: string;
  billingAddress?: string;
  vatNumber?: string;
  contactPhone?: string;
  tempPassword?: string;
}): Promise<{ success: boolean; message: string; partner_id?: string; user_id?: string }> => {
  try {
    const { data: result, error } = await supabase.rpc('admin_create_partner_with_auth', {
      p_business_name: data.businessName,
      p_contact_email: data.contactEmail,
      p_contact_name: data.contactName,
      p_billing_address: data.billingAddress || '',
      p_vat_number: data.vatNumber || '',
      p_contact_phone: data.contactPhone || '',
      p_temp_password: data.tempPassword || 'TravelHero2024!'
    });

    if (error) {
      console.error('❌ Database function error:', error);
      return { 
        success: false, 
        message: `Database error: ${error.message}` 
      };
    }

    console.log('📦 Function response:', result);

    // The function returns an array with a single result object
    const resultData = Array.isArray(result) ? result[0] : result;

    if (!resultData) {
      return { 
        success: false, 
        message: 'No response from database function' 
      };
    }

    // Check if the operation was successful
    if (!resultData.success) {
      console.error('❌ Partner creation failed:', resultData.message);
      return { 
        success: false, 
        message: resultData.message || 'Partner creation failed' 
      };
    }

    // Fetch the created partner record
    const { data: partner, error: fetchError } = await supabase
      .from('partners')
      .select('*')
      .eq('id', resultData.partner_id)
      .single();

    if (fetchError || !partner) {
      console.error('❌ Failed to fetch created partner:', fetchError);
      return { 
        success: false, 
        message: 'Partner created but failed to retrieve details' 
      };
    }

    console.log('✅ Partner created successfully:', partner);
    return { 
      success: true, 
      message: 'Partner created successfully',
      partner_id: resultData.partner_id,
      user_id: partner.id
    };

  } catch (err) {
    console.error('❌ Unexpected error in createPartner:', err);
    const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
    return { 
      success: false, 
      message: errorMessage 
    };
  }
};

// Get partner statistics using database function with date filtering
export const getPartnerStatistics = async (
  partnerId: string,
  startDate?: Date,
  endDate?: Date
): Promise<{
  offersCount: number;
  activeOffersCount: number;
  totalRedemptions: number;
  totalRevenue: number;
  averageRedemptionValue: number;
  activeOfferRate: number;
  repeatCustomerRate: number;
  averageRedemptionAmountPerUser: number;
  totalRedemptionAmount: number;
}> => {
  try {
    const { data, error } = await supabase.rpc('get_partner_statistics_filtered', {
      partner_id_param: partnerId,
      start_date: startDate?.toISOString() || null,
      end_date: endDate?.toISOString() || null
    });

    if (error) throw error;

    const stats = data?.[0] || {
      total_active_offers: 0,
      active_offers: 0,
      total_redemptions: 0,
      total_savings: 0,
      average_redemption_value: 0,
      active_offer_rate: 0,
      repeat_customer_rate: 0,
      average_redemption_amount_per_user: 0,
      total_redemption_amount: 0
    };

    return {
      offersCount: stats.total_active_offers,
      activeOffersCount: stats.active_offers,
      totalRedemptions: stats.total_redemptions,
      totalRevenue: stats.total_savings,
      averageRedemptionValue: stats.average_redemption_value,
      activeOfferRate: stats.active_offer_rate,
      repeatCustomerRate: stats.repeat_customer_rate,
      averageRedemptionAmountPerUser: stats.average_redemption_amount_per_user,
      totalRedemptionAmount: stats.total_redemption_amount
    };
  } catch (error) {
    console.error('Error fetching partner statistics:', error);
    throw error;
  }
};

// Update offer (partner can edit their own offers)
export const updateOffer = async (offerId: string, offerData: any): Promise<void> => {
  try {
    const { error } = await supabase
      .from('offers')
      .update({
        ...offerData,
        updated_at: new Date().toISOString()
      })
      .eq('id', offerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating offer:', error);
    throw error;
  }
};

// Delete offer (partner can delete their own offers)
export const deleteOffer = async (offerId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('offers')
      .delete()
      .eq('id', offerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting offer:', error);
    throw error;
  }
};

// Activate offer (change status to active)
export const activateOffer = async (offerId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('offers')
      .update({
        status: 'active',
        updated_at: new Date().toISOString()
      })
      .eq('id', offerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error activating offer:', error);
    throw error;
  }
};

// Deactivate offer (change status to inactive)
export const deactivateOffer = async (offerId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('offers')
      .update({
        status: 'inactive',
        updated_at: new Date().toISOString()
      })
      .eq('id', offerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deactivating offer:', error);
    throw error;
  }
};

// Get total offers count across all partners
export const getTotalOffersCount = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('offers')
      .select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching total offers count:', error);
    throw error;
  }
};

// Update partner information
export const updatePartner = async (partnerId: string, partnerData: Partial<Partner>): Promise<void> => {
  try {
    const { error } = await supabase
      .from('partners')
      .update({
        ...partnerData,
        updated_at: new Date().toISOString()
      })
      .eq('id', partnerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating partner:', error);
    throw error;
  }
};

// Pre-approve partner application (new status before full approval)
export const preApprovePartner = async (
  partnerId: string,
  preApprovedBy: string
): Promise<void> => {
  try {
    // Call the simplified database function that only updates status
    const { data, error } = await supabase.rpc('pre_approve_partner_application', {
      partner_id_param: partnerId
    });

    if (error) throw error;

    // Check function result
    if (!data || data.length === 0 || !data[0].success) {
      throw new Error(data?.[0]?.message || 'Failed to pre-approve partner');
    }
  } catch (error) {
    console.error('Error pre-approving partner:', error);
    throw error;
  }
};

// Schedule partner call after pre-approval
export const schedulePartnerCall = async (
  partnerId: string,
  scheduledDate: Date,
  meetingLink?: string
): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc('schedule_partner_call', {
      p_partner_id: partnerId,
      p_scheduled_date: scheduledDate.toISOString(),
      p_meeting_link: meetingLink || null
    });

    if (error) throw error;

    if (!data || data.length === 0 || !data[0].success) {
      throw new Error(data?.[0]?.message || 'Failed to schedule call');
    }

    return data[0].schedule_id;
  } catch (error) {
    console.error('Error scheduling partner call:', error);
    throw error;
  }
};

// Get partner call schedules
export const getPartnerCallSchedules = async (partnerId: string) => {
  try {
    const { data, error } = await supabase
      .from('partner_call_schedules')
      .select('*')
      .eq('partner_id', partnerId)
      .order('scheduled_date', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching partner call schedules:', error);
    throw error;
  }
};

// Get all pre-approved partners awaiting call scheduling
export const getPreApprovedPartners = async (): Promise<PartnerApplication[]> => {
  try {
    const { data: partners, error: partnersError } = await supabase
      .from('partners')
      .select('*')
      .eq('status', 'pre_approved')
      .order('pre_approved_date', { ascending: false });

    if (partnersError) throw partnersError;
    if (!partners) return [];

    const applicationsWithCounts = await Promise.all(
      partners.map(async (partner) => {
        const { count } = await supabase
          .from('offers')
          .select('*', { count: 'exact', head: true })
          .eq('partner_id', partner.id);

        return {
          partner,
          documents: [],
          offers_count: count || 0
        };
      })
    );

    return applicationsWithCounts;
  } catch (error) {
    console.error('Error fetching pre-approved partners:', error);
    throw error;
  }
};

// Delete partner application completely
export const deletePartnerApplication = async (partnerId: string): Promise<void> => {
  try {
    console.log('Starting partner deletion process for partner ID:', partnerId);
    
    // Step 1: Delete all related offers first (CASCADE manually)
    console.log('Step 1: Deleting related offers...');
    const { error: offersError, count: offersCount } = await supabase
      .from('offers')
      .delete()
      .eq('partner_id', partnerId);

    if (offersError) {
      console.error('Error deleting offers:', offersError);
      throw new Error(`Failed to delete partner offers: ${offersError.message}`);
    }
    
    console.log(`Deleted ${offersCount || 0} offers associated with partner`);

    // Step 2: Delete user profile if exists (to clean up auth linkage)
    console.log('Step 2: Deleting user profile if exists...');
    const { data: profile, error: profileFetchError } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('partner_id', partnerId)
      .maybeSingle();

    if (profileFetchError) {
      console.warn('Warning fetching user profile:', profileFetchError.message);
    }

    if (profile) {
      const { error: profileDeleteError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('partner_id', partnerId);
        
      if (profileDeleteError) {
        console.warn('Warning deleting user profile:', profileDeleteError.message);
      }
      console.log('User profile deleted successfully');
    }

    // Step 3: Finally delete the partner record
    console.log('Step 3: Deleting partner record...');
    const { error: partnerError } = await supabase
      .from('partners')
      .delete()
      .eq('id', partnerId);

    if (partnerError) {
      console.error('Error deleting partner:', partnerError);
      throw new Error(`Failed to delete partner: ${partnerError.message}`);
    }
    
    console.log('Partner deletion completed successfully');
  } catch (error) {
    console.error('Error in deletePartnerApplication:', error);
    throw error;
  }
};

// Delete partner
export const deletePartner = async (partnerId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🗑️ Starting partner deletion for ID:', partnerId);

    // Call the new database function that handles everything server-side
    const { data, error } = await supabase.rpc('admin_delete_partner_with_auth', {
      p_partner_id: partnerId
    });

    if (error) {
      console.error('❌ Database function error:', error);
      return { 
        success: false, 
        error: `Failed to delete partner: ${error.message}` 
      };
    }

    // Check result from database function
    const result = Array.isArray(data) ? data[0] : data;
    
    if (!result || !result.success) {
      console.error('❌ Partner deletion failed:', result?.message);
      return { 
        success: false, 
        error: result?.message || 'Unknown error occurred during deletion' 
      };
    }

    console.log('✅ Partner deleted successfully:', result.message);
    console.log(`📊 Deleted ${result.deleted_offers_count} offers, auth user: ${result.deleted_auth_user}`);
    
    return { success: true };
  } catch (error) {
    console.error('❌ Unexpected error during partner deletion:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};

// Get all partner login credentials
export const getAllPartnerCredentials = async (): Promise<{
  id: string;
  businessName: string;
  emailLogin: string | null;
  contactEmail: string;
  contactPhone: string | null;
  businessAddress: string | null;
  status: string;
  partnerCode: string;
  createdAt: string;
}[]> => {
  try {
    const { data: partners, error } = await supabase
      .from('partners')
      .select('id, business_name, email_login, contact_email, contact_phone, business_address, status, partner_code, created_at')
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Transform snake_case to camelCase for easier consumption
    return (partners || []).map(partner => ({
      id: partner.id,
      businessName: partner.business_name,
      emailLogin: partner.email_login,
      contactEmail: partner.contact_email,
      contactPhone: partner.contact_phone,
      businessAddress: partner.business_address,
      status: partner.status,
      partnerCode: partner.partner_code,
      createdAt: partner.created_at
    }));
  } catch (error) {
    console.error('Error fetching partner credentials:', error);
    throw error;
  }
};

// NEW: Get partner offer capacity
export const getPartnerOfferCapacity = async (
  partnerId: string
): Promise<{
  canCreate: boolean;
  currentCount: number;
  maxAllowed: number;
  remaining: number;
}> => {
  try {
    const { data, error } = await supabase.rpc('can_partner_create_offer', {
      p_partner_id: partnerId
    });

    if (error) throw error;

    if (!data || data.length === 0) {
      return {
        canCreate: false,
        currentCount: 0,
        maxAllowed: 5,
        remaining: 0
      };
    }

    const capacity = data[0];
    return {
      canCreate: capacity.can_create,
      currentCount: capacity.current_count,
      maxAllowed: capacity.max_allowed,
      remaining: capacity.remaining
    };
  } catch (error) {
    console.error('Error fetching partner offer capacity:', error);
    throw error;
  }
};

function partnerService(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: partnerService is not implemented yet.', args);
  return null;
}

export default partnerService;